import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  Button,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Delete, OpenInNew, BurstMode, PhotoLibrary, Crop, Visibility, BarChart as BarChartIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import PurgeOldGalleries from '../shared/PurgeOldGalleries';
import MPhotoSectionTabs, { ADMIN_GALLERY_FILTER_STICKY_TOP } from '../shared/MPhotoSectionTabs';
import DeviceFilterSelect from '../shared/DeviceFilterSelect';
import GalleryDateFilter from '../shared/GalleryDateFilter';
import GalleryStatsPanel from '../shared/GalleryStatsPanel';
import { userCardEnterSx } from '../shared/UserPageShell';
import { filterMachinesByAppProduct } from '../../utils/deviceMgmtFeatures';
import { useI18n } from '../../i18n/useI18n';
import { rewriteToCdn } from '../../utils/cdn';

interface AdminMPhotoMonoProps {
  userId: string;
}

type MonoTab = 'sub-photos' | 'frames' | 'photos' | 'stats';

const PAGE_SIZE = 10;

const toIso = (datetimeLocal: string): string => {
  if (!datetimeLocal) return '';
  const localDate = new Date(datetimeLocal);
  return Number.isNaN(localDate.getTime()) ? '' : localDate.toISOString();
};

const AdminMPhotoMono: React.FC<AdminMPhotoMonoProps> = ({ userId }) => {
  const { pages: t } = useI18n();
  const [tab, setTab] = useState<MonoTab>('photos');
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [subPhotos, setSubPhotos] = useState<api.MonoAsset[]>([]);
  const [frames, setFrames] = useState<api.MonoAsset[]>([]);
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [photosLoaded, setPhotosLoaded] = useState(false);

  const [monoPhotoGroups, setMonoPhotoGroups] = useState<api.MonoPhotoGallery[]>([]);
  const [photosPage, setPhotosPage] = useState(1);
  const [photosHasMore, setPhotosHasMore] = useState(true);
  const [photosLoadingMore, setPhotosLoadingMore] = useState(false);
  const [machineCodeFilter, setMachineCodeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [userMachines, setUserMachines] = useState<api.MachineInfo[]>([]);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const monoMachines = React.useMemo(
    () => filterMachinesByAppProduct(userMachines, 'mono'),
    [userMachines]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getAdminUserMachines(userId);
        if (!cancelled) setUserMachines(res.machines || []);
      } catch (error) {
        console.error('Failed to load machines for admin mono photo filter:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const fetchAssets = useCallback(async () => {
    try {
      setAssetsLoading(true);
      const [subPhotoData, frameData] = await Promise.all([
        api.getAdminUserMonoSubPhotos(userId),
        api.getAdminUserMonoFrames(userId),
      ]);
      setSubPhotos(subPhotoData || []);
      setFrames(frameData || []);
      setAssetsLoaded(true);
    } catch (error) {
      console.error('Error loading admin mono assets:', error);
      toast.error('Không thể tải danh sách M-Photo Mono');
    } finally {
      setAssetsLoading(false);
    }
  }, [userId]);

  const fetchPhotosFirstPage = useCallback(async () => {
    try {
      setPhotosLoading(true);
      setMonoPhotoGroups([]);
      setPhotosPage(1);
      setPhotosHasMore(true);
      const startIso = toIso(startDate);
      const endIso = toIso(endDate);
      const result = await api.getAdminUserMonoPhotoGroups(userId, 1, PAGE_SIZE, {
        ...(machineCodeFilter ? { machineCode: machineCodeFilter } : {}),
        ...(startIso ? { startDate: startIso } : {}),
        ...(endIso ? { endDate: endIso } : {}),
      });
      const data = result?.data || [];
      setMonoPhotoGroups(data);
      const total = result?.pagination?.total ?? 0;
      setPhotosHasMore(data.length < total);
      setPhotosPage(2);
      setPhotosLoaded(true);
    } catch {
      toast.error('Không thể tải danh sách ảnh Mono');
    } finally {
      setPhotosLoading(false);
    }
  }, [userId, machineCodeFilter, startDate, endDate]);

  const fetchPhotosNextPage = useCallback(async (page: number) => {
    if (photosLoadingMore) return;
    try {
      setPhotosLoadingMore(true);
      const startIso = toIso(startDate);
      const endIso = toIso(endDate);
      const result = await api.getAdminUserMonoPhotoGroups(userId, page, PAGE_SIZE, {
        ...(machineCodeFilter ? { machineCode: machineCodeFilter } : {}),
        ...(startIso ? { startDate: startIso } : {}),
        ...(endIso ? { endDate: endIso } : {}),
      });
      const data = result?.data || [];
      setMonoPhotoGroups((prev) => {
        const next = [...prev, ...data];
        const total = result?.pagination?.total ?? 0;
        setPhotosHasMore(next.length < total);
        return next;
      });
      setPhotosPage(page + 1);
    } catch {
      toast.error('Không thể tải thêm ảnh');
    } finally {
      setPhotosLoadingMore(false);
    }
  }, [userId, photosLoadingMore, machineCodeFilter, startDate, endDate]);

  // Đổi user → reset cache
  useEffect(() => {
    setAssetsLoaded(false);
    setPhotosLoaded(false);
    setSubPhotos([]);
    setFrames([]);
    setMonoPhotoGroups([]);
    setMachineCodeFilter('');
    setStartDate('');
    setEndDate('');
    setTab('photos');
  }, [userId]);

  useEffect(() => {
    if ((tab === 'sub-photos' || tab === 'frames') && !assetsLoaded) {
      fetchAssets();
    }
  }, [tab, userId, assetsLoaded, fetchAssets]);

  useEffect(() => {
    if (tab === 'photos') {
      setPhotosLoaded(false);
    }
  }, [machineCodeFilter, startDate, endDate]);

  useEffect(() => {
    if (tab === 'photos' && !photosLoaded) {
      fetchPhotosFirstPage();
    }
  }, [tab, photosLoaded, fetchPhotosFirstPage]);

  useEffect(() => {
    if (tab !== 'photos') return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && photosHasMore && !photosLoadingMore) {
          fetchPhotosNextPage(photosPage);
        }
      },
      { rootMargin: '200px' }
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [tab, photosHasMore, photosLoadingMore, photosPage, fetchPhotosNextPage]);

  const handleDelete = async (id: string) => {
    try {
      if (tab === 'sub-photos') {
        await api.adminDeleteUserMonoSubPhoto(userId, id);
        setSubPhotos((prev) => prev.filter((item) => item._id !== id));
      } else if (tab === 'frames') {
        await api.adminDeleteUserMonoFrame(userId, id);
        setFrames((prev) => prev.filter((item) => item._id !== id));
      }
      toast.success('Đã xóa thành công');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Xóa thất bại');
    }
  };

  const handleDeletePhotoGroup = async (folderId: string) => {
    try {
      await api.adminDeleteUserMonoPhotoGroup(userId, folderId);
      setMonoPhotoGroups((prev) => prev.filter((g) => g.folderId !== folderId));
      toast.success('Đã xóa gallery Mono của user');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Xóa gallery thất bại');
    }
  };

  const assetItems = tab === 'sub-photos' ? subPhotos : frames;

  return (
    <Box>
      <MPhotoSectionTabs
        value={tab}
        onChange={setTab}
        accentColor="#0f3460"
        sticky
        options={[
          { value: 'photos', label: t.mPhotoMono.tabs.photos, icon: <PhotoLibrary fontSize="inherit" /> },
          { value: 'sub-photos', label: t.mPhotoMono.tabs.subPhotos, icon: <BurstMode fontSize="inherit" /> },
          { value: 'frames', label: t.mPhotoMono.tabs.frames, icon: <Crop fontSize="inherit" /> },
          { value: 'stats', label: t.mPhotoMono.tabs.stats, icon: <BarChartIcon fontSize="inherit" /> },
        ]}
      />

      <Box sx={{ display: tab === 'sub-photos' || tab === 'frames' ? 'block' : 'none' }}>
        {assetsLoading && !assetsLoaded ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            {assetItems.map((item, index) => (
              <Card key={item._id} sx={userCardEnterSx(index)}>
                <CardMedia component="img" height="200" image={item.url} alt={item.name} sx={{ objectFit: 'contain' }} />
                <CardActions>
                  <Button size="small" startIcon={<Visibility />} target="_blank" href={item.url}>
                    Xem
                  </Button>
                  <IconButton color="error" onClick={() => handleDelete(item._id)}>
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ display: tab === 'photos' ? 'block' : 'none' }}>
        <PurgeOldGalleries
          purgeKey={`admin:mono:${userId}`}
          toast
          requestBatch={() => api.adminPurgeExpiredGalleries(userId, 'mono')}
          onPurged={() => {
            void fetchPhotosFirstPage();
          }}
        />
        <Box
          sx={{
            mb: 1.5,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            position: { xs: 'sticky', md: 'static' },
            top: ADMIN_GALLERY_FILTER_STICKY_TOP,
            zIndex: (theme) => theme.zIndex.appBar - 3,
            bgcolor: 'background.default',
            pt: { xs: 0.5, md: 0 },
            pb: { xs: 1, md: 0 },
            mx: { xs: -0.5, md: 0 },
            px: { xs: 0.5, md: 0 },
          }}
        >
          <DeviceFilterSelect
            value={machineCodeFilter}
            onChange={setMachineCodeFilter}
            machines={monoMachines}
            accentColor="#0f3460"
            fullWidth
            minWidth={0}
            sx={{ mb: 0, maxWidth: { xs: '100%', sm: 420 }, flex: '1 1 220px' }}
          />
          <GalleryDateFilter
            startDate={startDate}
            endDate={endDate}
            onStartDate={setStartDate}
            onEndDate={setEndDate}
            onClear={() => {
              setStartDate('');
              setEndDate('');
            }}
            accentColor="#0f3460"
          />
        </Box>
        {photosLoading && !photosLoaded ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(3, minmax(0, 1fr))',
                  md: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              {monoPhotoGroups.map((group, index) => {
                const coverRaw = group.photos?.[0]?.url || '';
                const cover = rewriteToCdn(coverRaw);
                const created = group.createdAt
                  ? new Date(group.createdAt).toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' })
                  : '';
                return (
                  <Card
                    key={group.folderId}
                    elevation={0}
                    sx={{
                      width: '100%',
                      maxWidth: 300,
                      mx: 'auto',
                      borderRadius: 2.5,
                      overflow: 'hidden',
                      border: '1.5px solid',
                      borderColor: alpha('#0f3460', 0.28),
                      bgcolor: '#fff',
                      ...userCardEnterSx(index),
                    }}
                  >
                    <Box sx={{ position: 'relative', bgcolor: alpha('#0f3460', 0.06) }}>
                      {cover ? (
                        <CardMedia
                          component="img"
                          image={cover}
                          alt={group.folderId}
                          loading="lazy"
                          sx={{
                            aspectRatio: '3 / 4',
                            objectFit: 'contain',
                            width: '100%',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            aspectRatio: '3 / 4',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <PhotoLibrary sx={{ fontSize: 40, color: alpha('#0f3460', 0.35) }} />
                        </Box>
                      )}
                      <Chip
                        size="small"
                        label={`${group.photos?.length || 0} ảnh · ${group.printCount || 0} lượt in`}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          height: 22,
                          fontWeight: 800,
                          fontSize: '0.68rem',
                          bgcolor: alpha('#fff', 0.92),
                          color: '#0f3460',
                        }}
                      />
                    </Box>
                    <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        noWrap
                        title={group.folderId}
                        sx={{ fontSize: '0.78rem' }}
                      >
                        {group.folderId}
                      </Typography>
                      {created && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {created}
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions sx={{ px: 1, pt: 0, pb: 1, justifyContent: 'space-between' }}>
                      <Button
                        size="small"
                        startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                        href={group.viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
                      >
                        Xem
                      </Button>
                      <IconButton size="small" color="error" onClick={() => handleDeletePhotoGroup(group.folderId)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                );
              })}
            </Box>

            <Box ref={sentinelRef} sx={{ height: 40, mt: 2 }} />

            {photosLoadingMore && (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!photosHasMore && monoPhotoGroups.length > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 1 }}>
                Đã tải hết {monoPhotoGroups.length} gallery
              </Typography>
            )}
          </>
        )}
      </Box>

      {tab === 'stats' && (
        <GalleryStatsPanel channel="mono" userId={userId} accentColor="#0f3460" />
      )}
    </Box>
  );
};

export default AdminMPhotoMono;
